/**
 * Every read and control path this SPA calls, in one place.
 *
 * Two rules from plan 4.4 shape this file:
 *
 *   1. There is no tenant header of any kind. Tenancy is derived server side
 *      from the JWT subject joined to the `project_id` in the path. That is why
 *      every project scoped path here starts `/v1/projects/{projectId}/`.
 *   2. Path segments are customer controlled. Event names already contain
 *      spaces and colons in the estate, and a `#` would otherwise start the URL
 *      fragment, so every interpolated segment is percent encoded here rather
 *      than at each call site (acceptance A9.11).
 */

const seg = encodeURIComponent;

export const endpoints = {
  /**
   * Orgs, projects and roles, and the idempotent lazy bootstrap (plan 5.10.1).
   *
   * `tz` is the browser's IANA zone. The server reads it **only** when it
   * creates an org (D30) and validates it, so it is a preference and never a
   * tenancy input. It was always meant to be sent and never was, which is why
   * every org created so far reports in UTC: the fallback did its job silently,
   * and a Sydney team read every chart shifted ten hours.
   */
  me: (timeZone?: string) =>
    timeZone === undefined || timeZone === ''
      ? '/v1/me'
      : `/v1/me?tz=${seg(timeZone)}`,

  /** The one batched call that powers the Overview (plan 5.10). */
  queryBatch: (projectId: string) => `/v1/projects/${seg(projectId)}/query:batch`,

  /** Live tap. Poll with the cursor returned by the previous page. */
  live: (projectId: string) => `/v1/projects/${seg(projectId)}/live`,

  /** Event drilldown with up to five breakdown properties. */
  eventDetail: (projectId: string, eventName: string) =>
    `/v1/projects/${seg(projectId)}/events/${seg(eventName)}`,

  /**
   * The breakdown typeahead: which property keys were observed on this event
   * inside the range, and which of them are suppressed as identifying (5.7).
   *
   * This is a second call rather than a field on the drilldown because the
   * drilldown is answered per requested breakdown and the offer list is not:
   * asking for the list on every breakdown change would re-read the property
   * key index once per checkbox click.
   */
  eventProperties: (projectId: string, eventName: string) =>
    `/v1/projects/${seg(projectId)}/events/${seg(eventName)}/properties`,

  /** Session list, newest first, optionally filtered to one person. */
  sessions: (projectId: string) => `/v1/projects/${seg(projectId)}/sessions`,

  /**
   * Presigned chunk URLs for one session's recording.
   *
   * Fetched by the dashboard with the user's token, then handed to the player
   * on its own origin. The player never holds a credential (D31).
   */
  sessionReplay: (projectId: string, sessionId: string, afterSequence?: number | null) =>
    afterSequence === undefined || afterSequence === null
      ? `/v1/projects/${seg(projectId)}/sessions/${seg(sessionId)}/replay`
      : `/v1/projects/${seg(projectId)}/sessions/${seg(sessionId)}/replay?after=${String(
          afterSequence,
        )}`,

  /**
   * Submit a funnel, or get it straight back from the 15 minute cache.
   *
   * Answered as an async job rather than a tile, because a funnel is a
   * per person ordered scan of the Parquet lake and cannot come from a counter.
   * The response is a job envelope, so the caller polls `job` below.
   */
  funnelPreview: (projectId: string) => `/v1/projects/${seg(projectId)}/funnels/preview`,

  /**
   * Poll an async query job.
   *
   * The path scoped form, not the `/v1/jobs/{id}?project_id=` shorthand. Both
   * resolve scope identically, but this one cannot be called without naming the
   * project, so a job id can never be mistaken for a capability.
   */
  job: (projectId: string, jobId: string) =>
    `/v1/projects/${seg(projectId)}/jobs/${seg(jobId)}`,

  /**
   * Bounce rate and session duration over the range (cold tier).
   *
   * Separate from `sessions` because the list pages and this does not: a rate
   * computed from a page describes that page. Asked for once per range.
   */
  sessionMetrics: (projectId: string) =>
    `/v1/projects/${seg(projectId)}/sessions/metrics`,

  /**
   * Campaign, channel and referrer breakdowns for the range (cold tier).
   *
   * One request for all five breakdowns, because they come from one scan of the
   * Parquet lake. A call per dimension would read the same files five times to
   * draw one screen, and each answer would carry its own coverage, so the five
   * cards would be free to disagree about which days they were based on.
   *
   * The `limit` query parameter caps the rows per breakdown. Every breakdown
   * also returns its untruncated value count, so a capped card can say what it
   * is not showing rather than reading as the whole picture.
   */
  attribution: (projectId: string) => `/v1/projects/${seg(projectId)}/attribution`,

  /**
   * Who the visits were: country, device type, browser, OS, device, screen,
   * language, timezone, landing and exit page. One scan, same shape and `limit`
   * as `attribution`.
   */
  audience: (projectId: string) => `/v1/projects/${seg(projectId)}/audience`,

  /** Heatmaps (ADR 0034). `path` and `device` go in the query string. */
  heatmapPages: (projectId: string) => `/v1/projects/${seg(projectId)}/heatmaps`,
  pageHeatmap: (projectId: string) => `/v1/projects/${seg(projectId)}/heatmaps/page`,
  heatmapSnapshot: (projectId: string) => `/v1/projects/${seg(projectId)}/heatmaps/snapshot`,

  /** Person list and search by distinct id or email property. */
  people: (projectId: string) => `/v1/projects/${seg(projectId)}/people`,

  /** Person profile. */
  person: (projectId: string, distinctId: string) =>
    `/v1/projects/${seg(projectId)}/people/${seg(distinctId)}`,

  /** Person event timeline. DuckDB backed, so `meta.tier` is `cold`. */
  personTimeline: (projectId: string, distinctId: string) =>
    `/v1/projects/${seg(projectId)}/people/${seg(distinctId)}/timeline`,

  /**
   * First event state for the onboarding panel.
   *
   * There is deliberately no `onboardingTestEvent` entry here. Plan 5.10 lists
   * `POST /v1/projects/{id}/onboarding/test-event`, served by the collector, and
   * it is not built: the collector's only credential is a write key, whose
   * plaintext is shown once at creation and never stored, so no server side
   * component can present one on a tenant's behalf. Building it needs either a
   * system authenticated ingest route or a second credential, and that is a
   * security design decision, not an integration detail. Until it is made, the
   * escape hatch ships its diagnostics half. See docs/STATUS.md.
   */
  onboarding: (projectId: string) => `/v1/projects/${seg(projectId)}/onboarding`,

  /** Project settings. */
  project: (projectId: string) => `/v1/projects/${seg(projectId)}`,

  /** Write keys. Plaintext values reach `admin` and `owner` only (FR-AUTH-13). */
  keys: (projectId: string) => `/v1/projects/${seg(projectId)}/keys`,
  keyRotate: (projectId: string, keyId: string) =>
    `/v1/projects/${seg(projectId)}/keys/${seg(keyId)}/rotate`,
  keyRevoke: (projectId: string, keyId: string) =>
    `/v1/projects/${seg(projectId)}/keys/${seg(keyId)}/revoke`,
  key: (projectId: string, keyId: string) =>
    `/v1/projects/${seg(projectId)}/keys/${seg(keyId)}`,
} as const;
